import { useState, useEffect } from 'react';
import { X, FileText, CheckCircle, Loader2, Mail, ArrowRight, Gift } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadMagnetPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    // Check if user has already seen/submitted the popup
    const hasSeenPopup = localStorage.getItem('lead_magnet_seen');
    
    if (!hasSeenPopup) {
      // Show popup after 45 seconds or on exit intent
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 45000);

      // Exit intent detection
      const handleMouseLeave = (e) => {
        if (e.clientY <= 0 && !localStorage.getItem('lead_magnet_seen')) {
          setIsVisible(true);
        }
      };

      document.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('lead_magnet_seen', 'closed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/lead-magnet`, {
        name,
        email,
        lead_type: 'website_audit_guide'
      });

      setSubmitted(true);
      localStorage.setItem('lead_magnet_seen', 'submitted');
      toast.success('Check your email for the guide!');
      
      // Auto close after 5 seconds
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);

    } catch (error) {
      console.error('Error submitting lead:', error);
      toast.error('Failed to send guide. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fadeIn"
      onClick={handleClose}
    >
      <div 
        className="relative bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 bg-[var(--bg-dark)]/50 backdrop-blur-sm rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-dark)] transition-all z-10"
        >
          <X size={18} />
        </button>

        {/* Header Gradient */}
        <div className="h-2 w-full bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316]"></div>

        {!submitted ? (
          <div className="p-8">
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center mb-6 shadow-xl shadow-[#8B5CF6]/25">
              <Gift size={32} className="text-white" />
            </div>

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-3">
              Get Your Free{' '}
              <span className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] bg-clip-text text-transparent">
                Website Audit Guide
              </span>
            </h2>

            <p className="text-[var(--text-secondary)] mb-6">
              Discover 15 critical areas to evaluate your website's performance, SEO, and user experience. No strings attached!
            </p>

            {/* What's Included */}
            <div className="space-y-3 mb-6">
              {[
                'Performance & Speed Checklist',
                'SEO Optimization Guide',
                'UX Best Practices',
                'Security Assessment Tips',
                'Mobile Responsiveness Check'
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/20 flex items-center justify-center flex-shrink-0">
                    <CheckCircle size={12} className="text-[#10B981]" />
                  </div>
                  <span className="text-[var(--text-secondary)] text-sm">{item}</span>
                </div>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your Name"
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all"
                  required
                />
              </div>
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your Email Address"
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#3B82F6] focus:ring-1 focus:ring-[#3B82F6]/50 transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={20} />
                    Get Free Guide
                    <ArrowRight size={20} />
                  </>
                )}
              </button>
            </form>

            <p className="text-[var(--text-muted)] text-xs text-center mt-4">
              We respect your privacy. No spam, ever.
            </p>
          </div>
        ) : (
          /* Success State */
          <div className="p-8 text-center">
            <div className="w-20 h-20 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto mb-6 border-4 border-[#10B981]">
              <CheckCircle size={40} className="text-[#10B981]" />
            </div>
            
            <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-3">
              Check Your Inbox!
            </h2>
            
            <p className="text-[var(--text-secondary)] mb-6">
              We've sent the Website Audit Guide to <strong className="text-[var(--text-primary)]">{email}</strong>. 
              If you don't see it, check your spam folder.
            </p>

            <button
              onClick={handleClose}
              className="px-8 py-3 text-[var(--text-primary)] font-medium rounded-xl bg-[var(--bg-dark)] border border-[var(--border)] hover:border-[var(--border-hover)] transition-all"
            >
              Continue Browsing
            </button>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default LeadMagnetPopup;
